import { Box, CssBaseline, Drawer, Toolbar } from "@mui/material";
import React, { useEffect, useState, useRef, useContext } from "react";
import ConversationList from "./components/ConversationList";
import ActiveConversationWindow from "./components/ActiveConversationWindow";
import IChatMessage from "./types/interfaces/IChatMessage";
import downloadText from "./functions/chat-tools/downloadText";
import getDefaults from "./functions/defaults";
import HelpDialog from "./components/HelpDialog";
import TopBar from "./components/TopBar";
import useLocalStorage from "./hooks/useLocalStorage";
import GlobalSettings from "./components/GlobalSettings";
import { useChatOptions } from "./hooks/useChatOptions";
import { useFormFactor } from "./hooks/useFormFactor";
import { useSidebarOpen } from "./hooks/useSidebarOpen";
import { FormFactor } from "./types/enums/FormFactor";
import { PromotionSidebar } from "./components/promotion/PromotionSidebar";
import { useConversationListQuery, usePaginatedConversationList } from "./hooks/useConversationListQuery";
import {
  PersistenceAPI,
  PersistenceThread,
} from "./functions/requests/persistence";
import { useMutation, useQueryClient } from "react-query";
import { useOidcAccessToken } from "@axa-fr/react-oidc";
import { useOidcConfigurationName } from "./hooks/useOidcConfigurationName";
import PersistenceConversationListItem from "./components/PersistenceConversationListItem";
import { useParams } from "react-router-dom";
import getDefaultModelFromList from "./functions/comparisons-arrays/getDefaultModelFromList";
import { useAvailableModels } from "./hooks/useAvailableModels";
import { MigrationAssistantModal, MigrationContext } from "./components/MigrationAssistantModal";

export default function Chat() {
  const chatOptionsContext = useChatOptions();
  const chatOptions = chatOptionsContext.chatOptions;
  const formFactor = useFormFactor();
  const { sidebarOpen, sidebarWidth } = useSidebarOpen();
  const [activeConversationId, setActiveConversationId] = useState<
    string | null | undefined
  >(useParams()?.conversationId ?? null);
  const [helpOpen, setHelpOpen] = useState<boolean>(false);
  const [globalSettingsOpen, setGlobalSettingsOpen] = useState<boolean>(false);
  const [defaultSystemPrompt, setDefaultSystemPrompt] = useLocalStorage<string>(
    "defaultSystemPrompt",
    chatOptions.default_system_prompt,
  );
  const { availableModels } = useAvailableModels();
  const { oidcConfigurationName } = useOidcConfigurationName();
  const oidcAccessToken = useOidcAccessToken(oidcConfigurationName);
  const [useRagByDefault, setUseRagByDefault] = useLocalStorage<boolean>(
    "useRagByDefault",
    getDefaults().useRagByDefault,
  );

  // Use the paginated conversation list instead of the regular one
  const paginatedConversationList = usePaginatedConversationList(50);
  const { updateThread, addThread } = paginatedConversationList;
  const queryClient = useQueryClient();

  const hasCreatedConversation = useRef(false);
  
  // Get migration context to check if migration is active
  const migrationContext = useContext(MigrationContext);

  useEffect(() => {
    const convName =
      paginatedConversationList.threads?.find((i) => i.id === activeConversationId)
        ?.name ?? "Unnamed Conversation";
    let pageTitle =
      (process.env.REACT_APP_WHITELABEL_NAME ?? "Expedient") + " Chat";
    if (convName !== "Unnamed Conversation") {
      pageTitle =
        convName +
        " - " +
        (process.env.REACT_APP_WHITELABEL_NAME ?? "Expedient") +
        " Chat";
    }
    document.title = pageTitle;
  }, [paginatedConversationList.threads, activeConversationId]);

  useEffect(() => {
    if (paginatedConversationList.threads?.find(c => c.id === activeConversationId) == null && (paginatedConversationList.threads?.length ?? 0) > 0)  {
      setActiveConversationId(null);
    }
      return;
  }, [paginatedConversationList.threads]);

  useEffect(() => {
    // sets the url to the activeConversationId
    if (activeConversationId === null) return;
    const convName =
      paginatedConversationList.threads?.find((i) => i.id === activeConversationId)
        ?.name ?? "Unnamed Conversation";
    let pageTitle =
      (process.env.REACT_APP_WHITELABEL_NAME ?? "Expedient") + " Chat";
    if (convName !== "Unnamed Conversation") {
      pageTitle =
        convName +
        " - " +
        (process.env.REACT_APP_WHITELABEL_NAME ?? "Expedient") +
        " Chat";
    }
    document.title = pageTitle;
    if (window.location.pathname !== "/") {
      let loc = window.location.href;
      loc = loc.replace(window.location.pathname, "/" + activeConversationId);
      window.history.pushState({ pageTitle: pageTitle }, "", loc);
    } else {
      window.history.pushState(
        { pageTitle: pageTitle },
        "",
        window.location.href + activeConversationId,
      );
    }
  }, [activeConversationId]);

  const loadingFailedCallback = () => {
    if (activeConversationId !== null) setActiveConversationId(null);
    let pageTitle = (process.env.REACT_APP_WHITELABEL_NAME ?? "Expedient") + " Chat";
    window.history.pushState({ pageTitle: pageTitle }, "", window.location.origin);
  }

  const addConversationApi = useMutation({
    mutationFn: async () => {
      return PersistenceAPI.createThread(oidcAccessToken.accessToken, {
        name: "Unnamed Conversation",
        created_at: new Date().toISOString(),
        instructions: defaultSystemPrompt,
        rag: [],
        pii: false,
        model: getDefaultModelFromList(availableModels).internal_name,
      });
    },
    onMutate: async () => {
      // Cancel any outgoing refetches so they don't overwrite our optimistic update
      await queryClient.cancelQueries({ queryKey: ["paginatedConversationList"] });
      
      return { };
    },
    onSuccess: (newConversation) => {
      // Set the active conversation to the newly created one
      setActiveConversationId(newConversation.id);
      
      // Add the new conversation to our paginated list
      addThread(newConversation);
    },
    onError: () => {
      queryClient.invalidateQueries({
        queryKey: ["paginatedConversationList"],
        refetchActive: true
      });
    },
    onSettled: () => {
      // After success or failure, invalidate the cache
      queryClient.invalidateQueries({
        queryKey: ["paginatedConversationList"],
        refetchActive: false
      });
    }
  });

  // Handles initial app load conversation selection - moved after addConversationApi declaration
  useEffect(() => {
    // Only run this effect once when conversations are first loaded and no conversation is selected
    // Add check to ensure we don't trigger multiple conversation creations
    // Also check if migration is active - don't auto-create conversations during migration
    if (
      paginatedConversationList.threads && 
      paginatedConversationList.threads.length > 0 && 
      activeConversationId === null && 
      !hasCreatedConversation.current &&
      !migrationContext.isMigrationActive
    ) {
      // Sort conversations by most recent first
      const sortedConversations = [...paginatedConversationList.threads].sort(
        (a, b) => new Date(b.updated_at ?? "").getTime() - new Date(a.updated_at ?? "").getTime()
      );
      
      // Check if the most recent conversation is "Unnamed Conversation"
      if (sortedConversations[0].name === "Unnamed Conversation") {
        // Select it
        setActiveConversationId(sortedConversations[0].id);
      } else {
        // Create a new conversation and mark that we've done so
        hasCreatedConversation.current = true;
        addConversationApi.mutate();
      }
      
      // Focus will be handled later in a useEffect after the component renders
      setTimeout(() => {
        // Focus on the compose input area
        const inputElement = document.querySelector('.compose-input');
        if (inputElement) {
          (inputElement as HTMLElement).focus();
        }
      }, 500); // Small delay to ensure components are rendered
    }
  }, [paginatedConversationList.threads, activeConversationId, addConversationApi, migrationContext.isMigrationActive]);

  // Add a reset effect for the ref when the component unmounts or when page refreshes
  useEffect(() => {
    // Reset the ref when user manually changes active conversation
    if (activeConversationId !== null) {
      hasCreatedConversation.current = false;
    }
    
    return () => {
      // Reset when component unmounts
      hasCreatedConversation.current = false;
    };
  }, [activeConversationId]);

  const deleteConversationApi = useMutation({
    mutationFn: async (id: string | undefined) => {
      if (id === undefined) return;
      
      console.log(`Starting deletion of conversation with ID: ${id}`);
      
      // IMPORTANT: First, immediately perform DOM manipulation to hide the item
      const threadElement = document.querySelector(`[data-thread-id="${id}"]`);
      if (threadElement) {
        console.log("Found DOM element, removing from view immediately");
        // Hide immediately first to ensure visual feedback
        (threadElement as HTMLElement).style.display = "none";
      }
      
      // If this is the active conversation, set to null first
      if (activeConversationId === id) {
        setActiveConversationId(null);
        window.history.pushState((process.env.REACT_APP_WHITELABEL_NAME ?? "Expedient") + " Chat",
          "",
          window.location.origin,
        );
      }
      
      // Immediately update local state - we can't access internal hooks,
      // so we'll use the methods available through React Query
      queryClient.setQueriesData(
        { queryKey: ["paginatedConversationList"] },
        (old: any) => {
          if (Array.isArray(old)) {
            return old.filter((thread: any) => thread?.id !== id);
          }
          return old;
        }
      );
      
      // Finally perform the API call
      return PersistenceAPI.deleteThread(oidcAccessToken.accessToken, id);
    },
    onMutate: async (id: string | undefined) => {
      if (!id) return {};
      
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: ["paginatedConversationList"] });
      
      // Get the current threads from the cache
      const previousThreads = queryClient.getQueryData(["paginatedConversationList"]);
      
      // Optimistically update all caches that might have this thread
      queryClient.setQueriesData(
        { queryKey: ["paginatedConversationList"] },
        (old: any) => {
          if (Array.isArray(old)) {
            return old.filter((thread: any) => thread?.id !== id);
          }
          return old;
        }
      );
      
      // Don't try to access internal hook properties directly,
      // just update the general paginated conversation list query
      queryClient.setQueryData(
        ["paginatedConversationList"],
        (old: any) => {
          if (Array.isArray(old)) {
            return old.filter((thread: any) => thread?.id !== id);
          }
          return old;
        }
      );
      
      return { previousThreads };
    },
    onError: (err, id, context) => {
      console.error("Error deleting thread:", err);
      
      // Show error to user
      alert(`Error deleting conversation. Please try again.`);
      
      // Restore the previous threads on error
      if (context?.previousThreads) {
        queryClient.setQueryData(
          ["paginatedConversationList"],
          context.previousThreads
        );
      }
    },
    onSuccess: (result, id) => {
      console.log(`Successfully deleted thread ${id}`);
      
      // Also invalidate any queries for this specific thread's messages
      if (id) {
        queryClient.removeQueries({
          queryKey: ["chatMessageList", id]
        });
      }
    },
    onSettled: () => {
      // Force a refresh to ensure consistency
      paginatedConversationList.forceRefresh();
    },
  });

  const editConversationApi = useMutation({
    mutationFn: async (editedThread: PersistenceThread) => {
      if (editedThread.id === undefined) return;
      
      // Update document title if this is the active conversation
      if (activeConversationId === editedThread.id) {
        document.title =
          editedThread.name +
          " - " +
          (process.env.REACT_APP_WHITELABEL_NAME ?? "Expedient") +
          " Chat";
      }
      
      console.log("Updating thread on server:", {
        id: editedThread.id,
        name: editedThread.name,
        model: editedThread.model
      });
      
      // Add timestamp to ensure changes are detected
      const threadWithTimestamp = {
        ...editedThread,
        updated_at: new Date().toISOString()
      };
      
      // Ensure ID is defined before using it
      if (!threadWithTimestamp.id) {
        console.error("Cannot update thread: ID is undefined");
        throw new Error("Thread ID is undefined");
      }
      
      // Retry logic for persistence
      let attempts = 0;
      const maxAttempts = 3;
      
      while (attempts < maxAttempts) {
        try {
          attempts++;
          const result = await PersistenceAPI.updateThread(
            oidcAccessToken.accessToken,
            threadWithTimestamp.id, // Now we know this is defined
            threadWithTimestamp
          );
          
          // Verify the result
          if (result && result.name === threadWithTimestamp.name) {
            console.log(`Thread update successful (attempt ${attempts})`);
            return result;
          } else {
            console.warn("Thread update did not return expected result:", result);
            // Short delay before retry
            await new Promise(resolve => setTimeout(resolve, 500));
          }
        } catch (error) {
          console.error(`Error updating thread (attempt ${attempts}/${maxAttempts}):`, error);
          // Only retry on specific errors
          if (attempts < maxAttempts) {
            await new Promise(resolve => setTimeout(resolve, 500));
          } else {
            throw error; // Rethrow on final attempt
          }
        }
      }
      
      // Default fallback - ensure ID is defined
      const threadId = threadWithTimestamp.id; // We've already checked this is defined
      return PersistenceAPI.updateThread(
        oidcAccessToken.accessToken,
        threadId,
        threadWithTimestamp
      );
    },
    onMutate: async (editedThread) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: ["paginatedConversationList"] });
      
      // Update the thread in our local state immediately for a smoother experience
      if (editedThread.id) {
        updateThread(editedThread);
      }
      
      return { };
    },
    onError: (error) => {
      console.error("Thread update failed:", error);
      queryClient.invalidateQueries({
        queryKey: ["paginatedConversationList"],
        refetchActive: true
      });
    },
    onSuccess: (result, variables) => {
      console.log("Thread update confirmed successful:", {
        id: variables.id,
        name: variables.name,
        resultName: result?.name
      });
      
      // Schedule a verification check
      if (variables.id) {
        setTimeout(async () => {
          try {
            // Ensure ID is defined before verifying
            const threadId = variables.id;
            if (!threadId) {
              console.warn("Cannot verify thread update: ID is undefined");
              return;
            }
            
            const verifiedThread = await PersistenceAPI.getThreadById(
              oidcAccessToken.accessToken,
              threadId
            );
            
            if (verifiedThread && verifiedThread.name !== variables.name) {
              console.warn("Thread name verification failed - expected:", variables.name, "got:", verifiedThread.name);
              
              // One final attempt to fix the name
              try {
                // Ensure we still have a valid ID
                if (!threadId) return;
                
                const finalUpdate = await PersistenceAPI.updateThread(
                  oidcAccessToken.accessToken,
                  threadId,
                  {
                    ...verifiedThread,
                    name: variables.name,
                    updated_at: new Date().toISOString()
                  }
                );
                
                if (finalUpdate) {
                  console.log("Final thread update applied successfully");
                  updateThread(finalUpdate);
                }
              } catch (finalError) {
                console.error("Final thread update failed:", finalError);
              }
            } else {
              console.log("Thread update verification successful");
            }
          } catch (verifyError) {
            console.error("Error verifying thread update:", verifyError);
          }
        }, 2000);
      }
    },
    onSettled: () => {
      // Invalidate but don't trigger an immediate refetch
      queryClient.invalidateQueries({
        queryKey: ["paginatedConversationList"],
        refetchActive: false
      });
    },
  });

  const handleTranscriptDownloaded = (history: IChatMessage[]) => {
    const content = JSON.stringify(history, null, 2);
    const currentConversation = paginatedConversationList.threads?.find(
      (c) => c.id === activeConversationId,
    );
    downloadText(currentConversation?.name + ".json", content);
  };

  const handleOpenHelp = () => {
    setHelpOpen(true);
  };

  const handleCloseHelp = () => {
    setHelpOpen(false);
  };

  const handleGlobalSettingsOpen = () => {
    setGlobalSettingsOpen(true);
  };

  const handleGlobalSettingsClose = () => {
    setGlobalSettingsOpen(false);
  };

  const handleGlobalSettingsSave = (
    newDefaultSystemPrompt: string,
    newUseRagByDefault: boolean,
  ) => {
    setDefaultSystemPrompt(newDefaultSystemPrompt);
    setUseRagByDefault(newUseRagByDefault);
  };

  return (
    <>
      <HelpDialog open={helpOpen} onClose={handleCloseHelp} />
      <GlobalSettings
        open={globalSettingsOpen}
        currentDefaultSystemPrompt={defaultSystemPrompt}
        currentUseRagByDefault={useRagByDefault}
        onSave={handleGlobalSettingsSave}
        onClose={handleGlobalSettingsClose}
      />

      <Box>
        <Box>
          <CssBaseline />
          <MigrationAssistantModal />
          <TopBar
            onHelpOpened={handleOpenHelp}
            onGlobalSettingsOpened={handleGlobalSettingsOpen}
          />
          <Drawer
            sx={{
              width: formFactor === FormFactor.Mobile ? "100vw" : sidebarWidth,
              "& .MuiDrawer-paper": {
                width:
                  formFactor === FormFactor.Mobile ? "100vw" : sidebarWidth,
                boxSizing: "border-box",
                overflowX: "hidden",
              },
            }}
            open={sidebarOpen}
            variant="persistent"
            anchor="left"
          >
            <Toolbar />
            <ConversationList
              onConversationAdded={addConversationApi.mutate}
              onGlobalSettingsOpened={handleGlobalSettingsOpen}
              conversationQueryStatus={paginatedConversationList.isFetching}
              hasMoreConversations={paginatedConversationList.hasMore}
              isLoadingMore={paginatedConversationList.isFetching}
              onLoadMoreClicked={paginatedConversationList.loadMore}
            >
              {paginatedConversationList.threads
                ?.sort(
                  (a, b) =>
                    new Date(b.updated_at ?? "").getTime() -
                    new Date(a.updated_at ?? "").getTime(),
                )
                .map((c) => (
                  <PersistenceConversationListItem
                    conversation={c}
                    onConversationInfoChanged={editConversationApi.mutate}
                    onConversationDeleted={deleteConversationApi.mutate}
                    onConversationClicked={() => setActiveConversationId(c.id)}
                    active={c.id === activeConversationId}
                    key={c.id}
                  />
                ))}
            </ConversationList>
          </Drawer>
          <Box
            component="main"
            sx={{
              ml:
                formFactor === FormFactor.Mobile
                  ? 0
                  : sidebarOpen
                    ? `${sidebarWidth}px`
                    : 0,
              mt: 0,
              mb: 0,
              mr:
                formFactor > FormFactor.Tablet && chatOptions.use_promotions
                  ? `${sidebarWidth}px`
                  : 0,
              width:
                formFactor === FormFactor.Mobile
                  ? "100%"
                  : sidebarOpen
                    ? `calc(100% - ${formFactor > FormFactor.Tablet && chatOptions.use_promotions ? sidebarWidth * 2 : sidebarWidth}px)`
                    : `calc(100% - ${formFactor > FormFactor.Tablet && chatOptions.use_promotions ? sidebarWidth : 0}px)`,
            }}
          >
            {" "}
            {activeConversationId != null ? (
              <ActiveConversationWindow
                conversationId={activeConversationId}
                onTranscriptDownloaded={handleTranscriptDownloaded}
                defaultSystemPrompt={defaultSystemPrompt}
                useRagByDefault={useRagByDefault}
                onLoadingFailed={loadingFailedCallback}
              />
            ) : (
              <></>
            )}
          </Box>
          {formFactor > FormFactor.Tablet && chatOptions.use_promotions ? (
            <Drawer variant="permanent" anchor="right">
              <Toolbar />
              <PromotionSidebar />
            </Drawer>
          ) : (
            <></>
          )}
        </Box>
      </Box>
    </>
  );
}
